import * as Sentry from "@sentry/react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { AlertIcon, Button, Modal } from "@cividao/components"
import { copyToClipboard } from "../../../js/string"
import { useEthereum } from "../../../state/hooks/useEthereum"
import styles from "./index.module.css"
import { ErrorModalProps, ErrorModalState, ErrorModalStateType } from "./types"

export const ErrorModal = ({
  children,
  error,
  size = "s",
  autoHideCopyMessage = false,
  copyMessageDuration = 2000,
  ...modalProps
}: ErrorModalProps) => {
  const { t } = useTranslation("common", { keyPrefix: "modal.error" })
  const { wallet } = useEthereum()
  const [state, setState] = useState<ErrorModalState>({ type: ErrorModalStateType.Idle })

  useEffect(() => {
    if (state.type === ErrorModalStateType.ReportSent && state.copied && autoHideCopyMessage) {
      const timer = setTimeout(() => {
        setState({ ...state, copied: false })
      }, copyMessageDuration)
      return () => clearTimeout(timer)
    }
  }, [state, autoHideCopyMessage, copyMessageDuration])

  const handleCopy = () => {
    if (state.type !== ErrorModalStateType.ReportSent) return
    copyToClipboard(state.reportId).then(() => setState({ ...state, copied: true }))
  }

  const handleSendToSupport = () => {
    setState({ type: ErrorModalStateType.SendingReport })
    const id = Sentry.captureException(error.error, {
      user: { id: wallet.account },
      tags: { code: error.code, wallet: wallet.account },
      extra: { domainError: error.error, wallet: wallet.serialize() },
    })

    setTimeout(() => {
      setState({ type: ErrorModalStateType.ReportSent, reportId: id, copied: false })
    }, 1000)
  }

  const isReportSent = state.type === ErrorModalStateType.ReportSent
  const isLoading = state.type === ErrorModalStateType.SendingReport
  const buttonAction = isReportSent ? handleCopy : handleSendToSupport
  const isDisabled = isLoading || (isReportSent && state.copied)

  const buttonText = isReportSent ? (state.copied ? t("reportIdCopied") : t("copyReportId")) : t("sendToSupport")

  const errorContent = isReportSent
    ? `${t("reportIdDescription")}: ${state.reportId}`
    : `${t("description")}: ${error.error.name}`

  return (
    <Modal {...modalProps} title={t("title")} size={size}>
      <div className={styles.container}>
        <div className={styles.header}>
          <span className={styles.iconWrapper}>
            <AlertIcon />
          </span>
          <h3 className={styles.title}>{t("title")}</h3>
        </div>

        <div className={styles.errorSection}>
          <div className={styles.errorContent}>
            <p className={styles.errorText}>{errorContent}</p>
          </div>
        </div>
        {children && <div className={styles.childrenContainer}>{children}</div>}

        <div className={styles.footer}>
          <Button isFull onClick={buttonAction} isDisabled={isDisabled} isLoading={isLoading}>
            {buttonText}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
